<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import ConfigForm from '../components/ConfigForm.vue';
import StudioSection from '../components/StudioSection.vue';
import { createConfigDraft } from '../components/configDraft';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import SidePanel from 'dashboard/components-next/side-panel/SidePanel.vue';
import AiBotPersonaTemplatesAPI from 'dashboard/api/aiBot/personaTemplates';
import AiBotPromptPreviewAPI from 'dashboard/api/aiBot/promptPreview';

const props = defineProps({
  bot: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

// Shared ConfigForm draft — same object the Save button submits.
const configState = createConfigDraft(props.bot);
const { draft, fieldError: errorFor } = configState;

const PLACEHOLDERS = [
  '{bot_name}',
  '{brand_name}',
  '{salutation}',
  '{contact_name}',
];
const INSTRUCTIONS_CHAR_LIMIT = 12000;

const identityFields = [
  {
    path: 'persona.bot_name',
    label: 'AI_AGENT.PERSONA.FIELDS.BOT_NAME',
    type: 'text',
  },
  {
    path: 'persona.brand_name',
    label: 'AI_AGENT.PERSONA.FIELDS.BRAND_NAME',
    type: 'text',
    help: 'AI_AGENT.PERSONA.FIELDS.BRAND_NAME_HELP',
  },
  {
    path: 'persona.salutation',
    label: 'AI_AGENT.PERSONA.FIELDS.SALUTATION',
    type: 'text',
  },
  {
    path: 'persona.role',
    label: 'AI_AGENT.PERSONA.FIELDS.ROLE',
    type: 'text',
  },
  {
    path: 'persona.tone',
    label: 'AI_AGENT.PERSONA.FIELDS.TONE',
    type: 'select',
    options: [
      { value: 'friendly', label: t('AI_AGENT.PERSONA.TONE_OPTIONS.FRIENDLY') },
      { value: 'formal', label: t('AI_AGENT.PERSONA.TONE_OPTIONS.FORMAL') },
      { value: 'casual', label: t('AI_AGENT.PERSONA.TONE_OPTIONS.CASUAL') },
    ],
  },
  {
    path: 'persona.language',
    label: 'AI_AGENT.PERSONA.FIELDS.LANGUAGE',
    type: 'select',
    options: [
      { value: 'id', label: t('AI_AGENT.PERSONA.LANGUAGE_OPTIONS.ID') },
      { value: 'en', label: t('AI_AGENT.PERSONA.LANGUAGE_OPTIONS.EN') },
      { value: 'auto', label: t('AI_AGENT.PERSONA.LANGUAGE_OPTIONS.AUTO') },
    ],
  },
  {
    path: 'persona.reply_char_limit',
    label: 'AI_AGENT.PERSONA.FIELDS.REPLY_CHAR_LIMIT',
    type: 'number',
    help: 'AI_AGENT.PERSONA.FIELDS.REPLY_CHAR_LIMIT_HELP',
  },
  {
    path: 'persona.address_by_name',
    label: 'AI_AGENT.PERSONA.FIELDS.ADDRESS_BY_NAME',
    type: 'switch',
  },
  {
    path: 'persona.admit_bot',
    label: 'AI_AGENT.PERSONA.FIELDS.ADMIT_BOT',
    type: 'switch',
    help: 'AI_AGENT.PERSONA.FIELDS.ADMIT_BOT_HELP',
  },
];

const styleFields = [
  {
    path: 'persona.styles.complaint',
    label: 'AI_AGENT.PERSONA.STYLES.COMPLAINT',
  },
  { path: 'persona.styles.promo', label: 'AI_AGENT.PERSONA.STYLES.PROMO' },
  {
    path: 'persona.styles.greeting',
    label: 'AI_AGENT.PERSONA.STYLES.GREETING',
  },
];

// The AI engine itself (provider/model) is managed by the platform; accounts only tune it.
const llmFields = [
  {
    path: 'llm.temperature',
    label: 'AI_AGENT.LLM.FIELDS.TEMPERATURE',
    type: 'number',
  },
  {
    path: 'llm.max_tokens',
    label: 'AI_AGENT.LLM.FIELDS.MAX_TOKENS',
    type: 'number',
  },
  {
    path: 'llm.history_turns',
    label: 'AI_AGENT.LLM.FIELDS.HISTORY_TURNS',
    type: 'number',
  },
  {
    path: 'llm.history_chars',
    label: 'AI_AGENT.LLM.FIELDS.HISTORY_CHARS',
    type: 'number',
  },
];

const pathValue = path =>
  path.split('.').reduce((acc, key) => acc?.[key], draft);

const updatePath = (path, value) => {
  const keys = path.split('.');
  let target = draft;
  keys.slice(0, -1).forEach(key => {
    if (typeof target[key] !== 'object' || target[key] === null) {
      target[key] = {};
    }
    target = target[key];
  });
  target[keys[keys.length - 1]] = value;
};

const numberValue = path => {
  const value = pathValue(path);
  return value === undefined || value === null ? '' : value;
};

const updateNumber = (path, value) => {
  updatePath(path, value === '' || value === undefined ? null : Number(value));
};

const appendPlaceholder = token => {
  updatePath(
    'persona.system_prompt',
    `${pathValue('persona.system_prompt') || ''}${token}`
  );
};

// Persona templates
const templates = ref([]);
const selectedTemplateId = ref('');
const templateConfirmDialogRef = ref(null);
const pendingTemplate = ref(null);

const templateOptions = computed(() =>
  templates.value.map(template => ({
    value: template.id,
    label: template.name,
  }))
);

const loadTemplates = async () => {
  try {
    const { data } = await new AiBotPersonaTemplatesAPI(props.bot.id).get();
    templates.value = data;
  } catch (error) {
    templates.value = [];
  }
};

const confirmTemplateReplace = () => {
  if (pendingTemplate.value) {
    updatePath('persona.system_prompt', pendingTemplate.value.content);
    pendingTemplate.value = null;
  }
  templateConfirmDialogRef.value.close();
};

const onSelectTemplate = templateId => {
  const template = templates.value.find(item => item.id === templateId);
  selectedTemplateId.value = '';
  if (!template) return;
  if (pathValue('persona.system_prompt')) {
    pendingTemplate.value = template;
    templateConfirmDialogRef.value.open();
  } else {
    updatePath('persona.system_prompt', template.content);
  }
};

// Instruction blocks — only create the array on first add so opening the tab
// never marks the draft dirty.
const instructions = computed(() =>
  Array.isArray(draft.instructions) ? draft.instructions : []
);

const addInstruction = () => {
  if (!Array.isArray(draft.instructions)) draft.instructions = [];
  draft.instructions.push({ title: '', content: '', enabled: true });
};

const removeInstruction = index => {
  instructions.value.splice(index, 1);
};

const moveInstruction = (index, delta) => {
  const target = index + delta;
  if (target < 0 || target >= instructions.value.length) return;
  const [item] = instructions.value.splice(index, 1);
  instructions.value.splice(target, 0, item);
};

const systemPromptChars = computed(
  () => (pathValue('persona.system_prompt') || '').length
);

const instructionsChars = computed(() =>
  instructions.value.reduce(
    (sum, instruction) =>
      sum +
      (instruction.content || '').length +
      (instruction.title || '').length,
    0
  )
);

// Prompt preview drawer
const showPreviewPanel = ref(false);
const previewMessage = ref('');
const preview = ref(null);
const isPreviewLoading = ref(false);

const openPreview = async () => {
  isPreviewLoading.value = true;
  showPreviewPanel.value = true;
  try {
    const { data } = await new AiBotPromptPreviewAPI(props.bot.id).get({
      message: previewMessage.value,
    });
    preview.value = data;
  } catch (error) {
    preview.value = null;
  } finally {
    isPreviewLoading.value = false;
  }
};

onMounted(loadTemplates);
</script>

<template>
  <ConfigForm
    :bot="bot"
    :state="configState"
    :fields="identityFields"
    :title="$t('AI_AGENT.PERSONA.SECTIONS.IDENTITY')"
    :description="$t('AI_AGENT.PERSONA.SECTIONS.IDENTITY_HELP')"
    icon="i-lucide-user-round"
  >
    <div class="flex flex-col gap-5">
      <StudioSection
        :title="$t('AI_AGENT.PERSONA.SECTIONS.BASE_INSTRUCTIONS')"
        :description="$t('AI_AGENT.PERSONA.SECTIONS.BASE_INSTRUCTIONS_HELP')"
        icon="i-lucide-scroll-text"
      >
        <template #actions>
          <Button
            type="button"
            icon="i-lucide-eye"
            faded
            slate
            sm
            :label="$t('AI_AGENT.PERSONA.PREVIEW.BUTTON')"
            @click="openPreview"
          />
        </template>
        <div class="flex flex-col gap-2">
          <span class="text-label-small text-n-slate-10">
            {{ $t('AI_AGENT.PERSONA.PLACEHOLDERS_LABEL') }}
          </span>
          <div class="flex flex-wrap gap-1.5">
            <button
              v-for="token in PLACEHOLDERS"
              :key="token"
              type="button"
              class="inline-flex items-center gap-1 px-2 py-1 font-mono transition-colors rounded-md text-label-small bg-n-brand/10 text-n-blue-11 hover:bg-n-brand/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand motion-reduce:transition-none"
              @click="appendPlaceholder(token)"
            >
              <span class="i-lucide-plus size-3" />
              {{ token }}
            </button>
          </div>
        </div>
        <div class="flex flex-col gap-1">
          <TextArea
            id="persona-system-prompt"
            :model-value="pathValue('persona.system_prompt') || ''"
            :label="$t('AI_AGENT.PERSONA.FIELDS.SYSTEM_PROMPT')"
            :placeholder="$t('AI_AGENT.PERSONA.SYSTEM_PROMPT_PLACEHOLDER')"
            :message="errorFor('persona.system_prompt')"
            :message-type="errorFor('persona.system_prompt') ? 'error' : 'info'"
            min-height="10rem"
            max-height="28rem"
            auto-height
            @update:model-value="updatePath('persona.system_prompt', $event)"
          />
          <div
            class="flex flex-wrap items-center justify-between gap-2 text-label-small text-n-slate-10"
          >
            <span>{{ $t('AI_AGENT.PERSONA.LEGACY_PLACEHOLDER_HINT') }}</span>
            <span data-testid="persona-system-prompt-count">
              {{ $t('AI_AGENT.PERSONA.CHAR_COUNT', { n: systemPromptChars }) }}
            </span>
          </div>
        </div>
        <div class="flex flex-col gap-1.5">
          <span class="text-label text-n-slate-12">
            {{ $t('AI_AGENT.PERSONA.TEMPLATE_LABEL') }}
          </span>
          <Select
            v-model="selectedTemplateId"
            :options="templateOptions"
            :placeholder="$t('AI_AGENT.PERSONA.TEMPLATE_PLACEHOLDER')"
            @update:model-value="onSelectTemplate"
          />
        </div>
      </StudioSection>

      <StudioSection
        :title="$t('AI_AGENT.PERSONA.SECTIONS.STYLES')"
        :description="$t('AI_AGENT.PERSONA.SECTIONS.STYLES_HELP')"
        icon="i-lucide-palette"
      >
        <TextArea
          v-for="field in styleFields"
          :id="`field-${field.path}`"
          :key="field.path"
          :model-value="pathValue(field.path) || ''"
          :label="$t(field.label)"
          auto-height
          @update:model-value="updatePath(field.path, $event)"
        />
      </StudioSection>

      <StudioSection
        :title="$t('AI_AGENT.PERSONA.SECTIONS.INSTRUCTIONS')"
        :description="$t('AI_AGENT.PERSONA.SECTIONS.INSTRUCTIONS_HELP')"
        icon="i-lucide-list-checks"
      >
        <template #actions>
          <span
            class="px-2 py-0.5 rounded-full text-label-small"
            :class="
              instructionsChars > INSTRUCTIONS_CHAR_LIMIT
                ? 'bg-n-ruby-9/10 text-n-ruby-11'
                : 'bg-n-alpha-2 text-n-slate-11'
            "
          >
            {{
              $t('AI_AGENT.PERSONA.INSTRUCTIONS_CHARS', {
                used: instructionsChars,
                limit: INSTRUCTIONS_CHAR_LIMIT,
              })
            }}
          </span>
        </template>
        <p
          v-if="!instructions.length"
          class="text-body-main text-n-slate-10"
          data-testid="persona-instructions-empty"
        >
          {{ $t('AI_AGENT.PERSONA.INSTRUCTION.EMPTY') }}
        </p>
        <div
          v-for="(instruction, index) in instructions"
          :key="index"
          class="flex flex-col gap-3 p-4 transition-opacity rounded-xl bg-n-alpha-1 outline outline-1 -outline-offset-1 outline-n-weak motion-reduce:transition-none"
          :class="{ 'opacity-60': !instruction.enabled }"
        >
          <div class="flex items-end gap-2">
            <span
              class="flex items-center justify-center mb-2 rounded-md shrink-0 size-6 bg-n-alpha-2 text-label-small text-n-slate-11"
            >
              {{ index + 1 }}
            </span>
            <div class="flex-1 min-w-0">
              <Input
                :id="`instruction-title-${index}`"
                v-model="instruction.title"
                :label="$t('AI_AGENT.PERSONA.INSTRUCTION.TITLE')"
              />
            </div>
            <div class="flex items-center gap-1 mb-1">
              <Switch v-model="instruction.enabled" class="mx-1" />
              <Button
                type="button"
                icon="i-lucide-arrow-up"
                slate
                ghost
                xs
                :disabled="index === 0"
                @click="moveInstruction(index, -1)"
              />
              <Button
                type="button"
                icon="i-lucide-arrow-down"
                slate
                ghost
                xs
                :disabled="index === instructions.length - 1"
                @click="moveInstruction(index, 1)"
              />
              <Button
                type="button"
                icon="i-woot-bin"
                slate
                ghost
                xs
                class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                @click="removeInstruction(index)"
              />
            </div>
          </div>
          <TextArea
            :id="`instruction-content-${index}`"
            v-model="instruction.content"
            :label="$t('AI_AGENT.PERSONA.INSTRUCTION.CONTENT')"
            auto-height
          />
        </div>
        <div>
          <Button
            type="button"
            icon="i-lucide-plus"
            faded
            slate
            sm
            :label="$t('AI_AGENT.PERSONA.INSTRUCTION.ADD')"
            @click="addInstruction"
          />
        </div>
      </StudioSection>

      <StudioSection
        :title="$t('AI_AGENT.LLM.HEADING')"
        :description="$t('AI_AGENT.LLM.HELP')"
        icon="i-lucide-sparkles"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-5">
          <template v-for="field in llmFields" :key="field.path">
            <div v-if="field.type === 'select'" class="flex flex-col gap-1.5">
              <span class="text-label text-n-slate-12">
                {{ $t(field.label) }}
              </span>
              <Select
                :model-value="pathValue(field.path) || ''"
                :options="field.options"
                :data-testid="`field-${field.path}`"
                @update:model-value="updatePath(field.path, $event)"
              />
            </div>
            <Input
              v-else
              :id="`field-${field.path}`"
              :type="field.type === 'number' ? 'number' : 'text'"
              :model-value="
                field.type === 'number'
                  ? numberValue(field.path)
                  : pathValue(field.path) || ''
              "
              :label="$t(field.label)"
              :placeholder="field.placeholder ? $t(field.placeholder) : ''"
              :message="errorFor(field.path)"
              :message-type="errorFor(field.path) ? 'error' : 'info'"
              @update:model-value="
                field.type === 'number'
                  ? updateNumber(field.path, $event)
                  : updatePath(field.path, $event)
              "
            />
          </template>
        </div>
      </StudioSection>
    </div>
    <SidePanel
      v-if="showPreviewPanel"
      :title="$t('AI_AGENT.PERSONA.PREVIEW.TITLE')"
      width="2xl"
      @close="showPreviewPanel = false"
    >
      <div class="flex flex-col gap-4 p-6">
        <div class="flex items-end gap-2">
          <div class="flex-1 min-w-0">
            <Input
              id="preview-message"
              v-model="previewMessage"
              :label="$t('AI_AGENT.PERSONA.PREVIEW.MESSAGE_LABEL')"
            />
          </div>
          <Button
            type="button"
            sm
            icon="i-lucide-refresh-cw"
            :label="$t('AI_AGENT.PERSONA.PREVIEW.REFRESH')"
            :is-loading="isPreviewLoading"
            @click="openPreview"
          />
        </div>
        <div
          v-if="preview"
          class="flex flex-col gap-3"
          data-testid="prompt-preview"
        >
          <div class="flex flex-wrap gap-1.5">
            <span
              v-for="section in preview.sections"
              :key="section.name"
              class="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-label-small bg-n-alpha-2"
            >
              <span class="text-n-slate-11">{{ section.name }}</span>
              <span class="text-n-slate-12">{{ section.chars }}</span>
            </span>
          </div>
          <pre
            class="p-4 text-sm whitespace-pre-wrap rounded-lg text-n-slate-12 bg-n-solid-2 outline outline-1 -outline-offset-1 outline-n-weak"
            >{{ preview.system_prompt }}</pre
          >
          <p class="text-label-small text-n-slate-10">
            {{
              $t('AI_AGENT.PERSONA.PREVIEW.HISTORY_TURNS', {
                n: preview.history_turns,
              })
            }}
          </p>
        </div>
      </div>
    </SidePanel>
    <Dialog
      ref="templateConfirmDialogRef"
      type="alert"
      :title="$t('AI_AGENT.PERSONA.TEMPLATE_CONFIRM.TITLE')"
      :description="$t('AI_AGENT.PERSONA.TEMPLATE_CONFIRM.MESSAGE')"
      :confirm-button-label="$t('AI_AGENT.PERSONA.TEMPLATE_CONFIRM.YES')"
      :cancel-button-label="$t('AI_AGENT.PERSONA.TEMPLATE_CONFIRM.NO')"
      @confirm="confirmTemplateReplace"
    />
  </ConfigForm>
</template>

